import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, TextField, Autocomplete, Typography, Alert, Divider,
  LinearProgress, Chip, IconButton, Tooltip, createFilterOptions,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import apiService from '../services/api';
import { formatStatusLabel } from '../utils/statusLabel';

// Create Account modal for the admin Accounts page. Same feature that used to live
// in the Org Services portal — it launches an account-factory run through the admin
// customer API (Org-Services gated) and tracks progress in place. The server-side
// handback continues even if this modal is closed, so closing is always allowed.

// Statuses that stop the poll loop. `awaiting_payment_method` is the factory's
// durable pause: not terminal server-side (an operator adds the card by hand and the
// run resumes), but there is nothing further this modal can show, and treating it as
// non-terminal left the admin watching an in-progress spinner forever with no account
// id — the account exists and is billing by then.
const STOP_POLLING = ['completed', 'failed', 'awaiting_payment_method'];
const POLL_INTERVAL_MS = 4000;
const BLUE = '#4698D3';
const BLUE_DARK = '#3580B8';
const METRO = 'Metropolis, sans-serif';

// Light client-side email sanity check; the server validates authoritatively.
const looksLikeEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

// Filter the master-account picker on BOTH the account name and the 12-digit ID —
// getOptionLabel only exposes the ID, so without this the list is ID-searchable only.
const accountFilter = createFilterOptions({
  stringify: (o) => (typeof o === 'string' ? o : `${o.name || ''} ${o.accountId}`),
});

export default function CreateAccountModal({ open, onClose, onCreated }) {
  const [accountName, setAccountName] = useState('');
  const [rootEmail, setRootEmail] = useState('');
  const [ownerEmailsRaw, setOwnerEmailsRaw] = useState('');
  const [adminRoleName, setAdminRoleName] = useState('');
  const [masterAccount, setMasterAccount] = useState(''); // resolved 12-digit account ID
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [operationId, setOperationId] = useState(null);
  const [op, setOp] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  // An operation is "in flight" once we have an id and it has not reached a state we
  // stop polling on — used to drive polling and the "Creating…" label.
  const active = !!operationId && !(op && STOP_POLLING.includes(op.status));
  // `started` stays true from submit through completion/failure, so the form and the
  // submit button lock as soon as a run begins and STAY locked once it's done — the
  // user closes/reopens the modal to create another. (`active` alone would re-enable
  // the button the moment the run finished.)
  const started = !!operationId;

  // Reset the form each time the modal opens and load the customer's onboarded
  // accounts for the master-account picker. A fetch failure degrades to typed-ID.
  useEffect(() => {
    if (!open) return undefined;
    setAccountName('');
    setRootEmail('');
    setOwnerEmailsRaw('');
    setAdminRoleName('');
    setMasterAccount('');
    setSubmitting(false);
    setSubmitError(null);
    setOperationId(null);
    setOp(null);
    setCopied(false);
    let cancelled = false;
    apiService.listFactoryAccounts()
      .then((res) => { if (!cancelled) setAccounts(res?.accounts || []); })
      .catch(() => { /* picker stays empty; the field still accepts a typed ID */ });
    return () => { cancelled = true; };
  }, [open]);

  // Poll-while-active: arms once per operation, clears itself once the status is one
  // we stop on. A transient fetch error keeps polling.
  useEffect(() => {
    if (!active || pollRef.current) return undefined;
    const tick = async () => {
      const res = await apiService.getFactoryAccountStatus(operationId).catch(() => null);
      if (!res) return; // transient error — keep polling
      setOp(res);
      if (STOP_POLLING.includes(res.status) && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    tick(); // fetch immediately so the first update doesn't wait a full interval
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return undefined;
  }, [active, operationId]);

  // Stop the interval on unmount.
  useEffect(() => () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const ownerEmails = useMemo(
    () => ownerEmailsRaw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean),
    [ownerEmailsRaw],
  );

  const emailValid = looksLikeEmail(rootEmail.trim());
  const masterAccountValid = /^\d{12}$/.test(masterAccount.trim());
  const canSubmit = accountName.trim().length > 0 && emailValid
    && adminRoleName.trim().length > 0 && masterAccountValid && !submitting && !started;

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await apiService.createFactoryAccount({
        accountName: accountName.trim(),
        rootEmail: rootEmail.trim(),
        ownerEmails,
        adminRoleName: adminRoleName.trim(),
        adminTrustAccount: masterAccount.trim(),
      });
      const opId = res?.operationId || res?.id;
      if (!opId) {
        throw new Error('The server did not return an operation id.');
      }
      setOp(null); // reset any prior operation's progress before tracking the new one
      setOperationId(opId);
      if (onCreated) onCreated(); // refresh the accounts list (appears once onboarded)
    } catch (e) {
      setSubmitError(e.message || 'Failed to start account creation.');
    } finally {
      setSubmitting(false);
    }
  };

  const completed = op && op.status === 'completed';
  const failed = op && op.status === 'failed';
  // The factory's durable pause: the AWS account exists, but the card could not be
  // added unattended and a CloudKeeper operator must finish it by hand.
  const paused = op && op.status === 'awaiting_payment_method';
  const progressPct = op && typeof op.progress === 'number' && op.progress >= 0 ? op.progress : null;
  // Amber matches the "action required" styling of the notification email operators
  // receive for the same event.
  const chipColor = failed ? '#E74C3C' : completed ? '#27AE60' : paused ? '#D97706' : BLUE;

  const copyId = () => {
    if (op?.awsAccountId && navigator.clipboard) {
      navigator.clipboard.writeText(op.awsAccountId)
        .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
        .catch(() => {});
    }
  };

  // Shared by the completed and paused alerts: in both cases the AWS account exists
  // and the admin needs its id. Rendered only when the id is known.
  const accountIdLine = op?.awsAccountId ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <strong>Please copy and keep this account ID:</strong>
      <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{op.awsAccountId}</Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy account ID'}>
        <IconButton size="small" onClick={copyId} sx={{ p: 0.25 }}>
          {copied
            ? <CheckIcon sx={{ fontSize: 16, color: '#27AE60' }} />
            : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddBusinessIcon sx={{ color: BLUE }} /> Create Account
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ fontFamily: METRO, color: '#666666', mb: 2.5 }}>
          Prism creates a brand-new AWS account under your management (payer) account and
          onboards it automatically once it&apos;s ready.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Account name"
            required
            size="small"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="Acme Prod"
            disabled={started}
            helperText="A friendly name for the new account (also used as its Prism name)."
          />
          <TextField
            label="Root email"
            required
            type="email"
            size="small"
            value={rootEmail}
            onChange={(e) => setRootEmail(e.target.value)}
            placeholder="aws-acme-prod@example.com"
            disabled={started}
            error={rootEmail.trim().length > 0 && !emailValid}
            helperText={
              rootEmail.trim().length > 0 && !emailValid
                ? 'Enter a valid email address.'
                : 'The unique root-user email for the new AWS account.'
            }
          />
          <TextField
            label="Owner emails (optional)"
            size="small"
            value={ownerEmailsRaw}
            onChange={(e) => setOwnerEmailsRaw(e.target.value)}
            placeholder="ops@example.com, platform@example.com"
            disabled={started}
            helperText="Comma- or space-separated. Recorded as the account owners in Prism."
          />

          <Divider />

          <TextField
            label="Admin role name"
            required
            size="small"
            value={adminRoleName}
            onChange={(e) => setAdminRoleName(e.target.value)}
            placeholder="OrgAdminAccess"
            disabled={started}
            helperText="An IAM role created in the new account with AdministratorAccess, assumable from the master account below."
          />
          <Autocomplete
            freeSolo
            forcePopupIcon
            options={accounts}
            filterOptions={accountFilter}
            disabled={started}
            value={masterAccount || null}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.accountId)}
            isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o : o.accountId) === v}
            renderOption={(props, o) => (
              <li {...props} key={o.accountId}>{o.name ? `${o.name} — ${o.accountId}` : o.accountId}</li>
            )}
            onChange={(_, v) => setMasterAccount(v ? (typeof v === 'string' ? v.trim() : v.accountId) : '')}
            onInputChange={(_, v, reason) => { if (reason === 'input') setMasterAccount(v.trim()); }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Master account"
                required
                size="small"
                placeholder="Select an account or type a 12-digit account ID"
                error={masterAccount.trim().length > 0 && !masterAccountValid}
                helperText={
                  masterAccount.trim().length > 0 && !masterAccountValid
                    ? 'Enter a valid 12-digit AWS account ID.'
                    : 'The admin role trusts this account’s root — any principal in it can assume the role.'
                }
              />
            )}
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}

          {operationId && (
            <Box sx={{ mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: METRO, fontWeight: 600, color: '#333333' }}>
                  Progress
                </Typography>
                <Chip
                  size="small"
                  label={formatStatusLabel(op?.status || 'pending')}
                  sx={{ backgroundColor: `${chipColor}1A`, color: chipColor, fontWeight: 600, fontFamily: METRO }}
                />
              </Box>
              <LinearProgress
                variant={progressPct == null ? 'indeterminate' : 'determinate'}
                value={progressPct ?? 0}
                sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: chipColor } }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontFamily: METRO, color: '#666666' }}>
                {(op && op.progressLabel) || 'Starting…'}{progressPct != null ? ` — ${progressPct}%` : ''}
              </Typography>

              {completed && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  Account created.{' '}
                  {accountIdLine}
                  <Box sx={{ mt: 0.75 }}>
                    Prism onboarding (SAML identity provider + account registration) runs
                    automatically and may take a few minutes — the new account will then appear in
                    your accounts list.
                  </Box>
                </Alert>
              )}
              {paused && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  The AWS account was created, but the payment method could not be added
                  automatically.{' '}
                  {accountIdLine}
                  <Box sx={{ mt: 0.75 }}>
                    CloudKeeper has been notified and an operator will complete the payment
                    setup. Prism onboarding finishes once that is done, and the account will
                    then appear in your accounts list. You can close this dialog.
                  </Box>
                </Alert>
              )}
              {failed && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {op.failureReason || op.errorMessage || 'Account creation failed.'}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontFamily: METRO, color: '#666666' }}
        >
          {completed || failed ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={<AddBusinessIcon />}
          sx={{
            backgroundColor: BLUE,
            borderRadius: '6px',
            fontFamily: METRO,
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            '&:hover': { backgroundColor: BLUE_DARK },
            '&.Mui-disabled': { backgroundColor: '#BDBDBD', color: '#FFFFFF' },
          }}
        >
          {submitting ? 'Starting…' : active ? 'Creating…' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
